/**
 * BB ID の付け直し(CSV)(2026-10-12 発注者指示。D-205)。オペレーターマスタの「BB IDの付け直し」から開く。
 * ファイルを選ぶ → 照合の結果(新旧のID・まとめるマスタ・ほかのデータの書き換え件数)を確認 → 「付け直す」。
 * 書き込む直前に、今のオペレーターマスタを JSON で保存する(控え)。計算は bbReissue.ts、書き込みは bbReissueRepo.ts。
 */
import { Fragment, useMemo, useState } from 'react';
import Modal from '@core/components/Modal';
import { useToast } from '@core/components/Toast';
import { parseCsv, readCsvFile } from '@core/lib/csv';
import { todayStr } from '@core/lib/date';
import { BUSINESS_LABELS, PAY_TYPE_LABELS, WORK_STYLE_CODE_LABELS, type Operator } from '@core/lib/types';
import { formatCount } from '@core/utils/formatters';
import { downloadCsv } from './csvImport';
import { parseReissueCsv, planReissue, type ReissueParseError, type ReissuePlan } from './bbReissue';
import { applyReissue, backupJson, REISSUE_COLLECTION_LABELS, scanRefs, type RefPlan } from './bbReissueRepo';

function downloadJson(fileName: string, content: string): void {
  const url = URL.createObjectURL(new Blob([content], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.hidden = true;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

const csvCell = (s: string) => (/[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s);

export default function BbReissueModal({ operators, userId, onClose, onDone }: { operators: Operator[]; userId: string; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [fileName, setFileName] = useState('');
  const [parseErrors, setParseErrors] = useState<ReissueParseError[]>([]);
  const [plan, setPlan] = useState<ReissuePlan | null>(null);
  const [refs, setRefs] = useState<RefPlan | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState<'' | 'scan' | 'apply'>('');

  async function pick(file: File | undefined) {
    setPlan(null);
    setRefs(null);
    setError('');
    setParseErrors([]);
    if (!file) return;
    setFileName(file.name);
    try {
      const table = parseCsv(await readCsvFile(file));
      const { rows, errors } = parseReissueCsv([table.headers, ...table.rows]);
      setParseErrors(errors);
      if (rows.length === 0) throw new Error('「在籍」の行がありません(1列目が「在籍」の行を読みます)');
      const p = planReissue(rows, operators);
      setPlan(p);
      if (errors.length === 0 && p.errorCount === 0) {
        setBusy('scan');
        setRefs(await scanRefs(p.persons));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'ファイルを読めませんでした');
    } finally {
      setBusy('');
    }
  }

  const summary = useMemo(() => {
    if (!plan) return null;
    const ps = plan.persons;
    return {
      persons: ps.length,
      ids: ps.reduce((n, p) => n + p.items.length, 0),
      update: ps.filter((p) => p.primary).length,
      create: ps.filter((p) => !p.primary).length,
      merge: ps.reduce((n, p) => n + p.merge.length, 0),
      warnings: ps.reduce((n, p) => n + p.warnings.length, 0),
    };
  }, [plan]);

  const blocked = !plan || !refs || parseErrors.length > 0 || plan.errorCount > 0;

  function saveMapping() {
    if (!plan) return;
    const lines = [['番号', '名前', 'CSVの名前', '出勤形態', '報酬区分', '架電内容', '旧BBID', '新BBID'].join(',')];
    for (const p of plan.persons)
      for (const i of p.items)
        lines.push(
          [String(p.no).padStart(3, '0'), p.name, i.csvName, WORK_STYLE_CODE_LABELS[i.workStyle], PAY_TYPE_LABELS[i.payType], BUSINESS_LABELS[i.business], i.oldBbId, i.newBbId].map(csvCell).join(','),
        );
    downloadCsv(`BBID新旧対応表_${todayStr()}.csv`, lines.join('\n') + '\n');
  }

  async function run() {
    if (blocked || !plan || !refs || !summary) return;
    if (!confirm(`${summary.persons}名・${summary.ids}件の BB ID を付け直します。旧BBIDは無効にして残し、${summary.merge > 0 ? `${summary.merge}件のマスタを1人にまとめます。` : ''}元に戻すときは、このあと保存される控え(JSON)を使います。よろしいですか?`)) return;
    setBusy('apply');
    try {
      downloadJson(`operators_backup_${todayStr()}.json`, backupJson(operators));
      const r = await applyReissue(plan.persons, refs, userId, operators.map((o) => o.op_code));
      toast(`BB IDを付け直しました(マスタ ${formatCount(r.updated)}名・新規 ${formatCount(r.created)}名・まとめ ${formatCount(r.merged)}件・ほかのデータ ${formatCount(r.refs)}件)`);
      onDone();
    } catch (e) {
      toast(e instanceof Error ? `付け直せませんでした: ${e.message}(もう一度実行すると続きから書き換えます)` : '付け直せませんでした');
      setBusy('');
    }
  }

  return (
    <Modal onClose={busy === 'apply' ? () => undefined : onClose} width="min(1040px, 94vw)">
      <h3 className="training-modal-title">BB IDの付け直し(CSV)</h3>
      <p className="ui-hint">
        在籍スタッフの CSV(在籍・名前・出勤形態・架電内容・報酬区分・現BBID)を選んでください。CSV の順に1人1番号を振り、新BBID = 形態コード＋番号3桁(例 HHA012)にします。名前の末尾が W の行は同じ人の不用品のIDです。旧BBIDは無効にして残すので、過去の成績と BlueBean 側の付け替え前の取込は同じ人に入ります。
      </p>
      <div className="training-actions">
        <input type="file" accept=".csv,text/csv" aria-label="在籍スタッフのCSV" onChange={(e) => void pick(e.target.files?.[0])} disabled={!!busy} />
      </div>
      {error && <p className="training-error">{error}</p>}
      {parseErrors.length > 0 && (
        <div className="training-error">
          読めない行があるため付け直せません:
          <ul>
            {parseErrors.map((e) => (
              <li key={e.line}>
                {e.line}行目: {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
      {plan && summary && (
        <>
          <p>
            {fileName}: <b>{formatCount(summary.persons)}名</b>・BB ID {formatCount(summary.ids)}件 ／ マスタを更新 {formatCount(summary.update)}名・新しく登録 {formatCount(summary.create)}名
            {summary.merge > 0 && <> ・ 1人にまとめるマスタ {formatCount(summary.merge)}件</>}
            {plan.errorCount > 0 && (
              <>
                {' '}
                ・ <b className="training-error">付け直せない理由 {formatCount(plan.errorCount)}件</b>
              </>
            )}
            {summary.warnings > 0 && <> ・ 注意 {formatCount(summary.warnings)}件</>}
          </p>
          <div className="report-scroll is-auto training-preview">
            <table className="report-table no-stack">
              <caption className="sr-only">付け直す内容</caption>
              <thead>
                <tr>
                  <th scope="col">番号</th>
                  <th scope="col">名前</th>
                  <th scope="col">種別</th>
                  <th scope="col">旧BBID</th>
                  <th scope="col">新BBID</th>
                  <th scope="col">マスタ</th>
                  <th scope="col">確認</th>
                </tr>
              </thead>
              <tbody>
                {plan.persons.map((p) => (
                  <Fragment key={p.no}>
                    {p.items.map((i, k) => (
                      <tr key={i.line} className={p.errors.length > 0 ? 'is-error' : undefined}>
                        <td className="is-nowrap">{k === 0 ? String(p.no).padStart(3, '0') : ''}</td>
                        <td className="is-nowrap">{k === 0 ? p.name : ''}</td>
                        <td className="is-nowrap">
                          {WORK_STYLE_CODE_LABELS[i.workStyle]}・{PAY_TYPE_LABELS[i.payType]}・{BUSINESS_LABELS[i.business]}
                        </td>
                        <td className="is-nowrap">{i.oldBbId || '—'}</td>
                        <td className="is-nowrap">
                          <b>{i.newBbId}</b>
                          {i.done && <span className="ui-hint">(付け直し済み)</span>}
                        </td>
                        <td className="is-nowrap">
                          {k === 0 ? (
                            p.primary ? (
                              <>
                                {p.primary.op_code} {p.primary.name}
                                {p.byName && <span className="ui-hint">(名前で照合)</span>}
                                {p.merge.map((m) => (
                                  <div key={m.id} className="ui-hint">
                                    ← {m.op_code} {m.name} をまとめる
                                  </div>
                                ))}
                              </>
                            ) : (
                              '新しく登録'
                            )
                          ) : (
                            ''
                          )}
                        </td>
                        <td className={p.errors.length > 0 ? 'training-error' : 'ui-hint'}>{k === 0 ? [...p.errors, ...p.warnings].join(' ／ ') : ''}</td>
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          {plan.notInCsv.length > 0 && (
            <details className="bb-reissue-extra">
              <summary>CSV に無い在籍中のマスタ: {formatCount(plan.notInCsv.length)}名(何もしません)</summary>
              <p className="ui-hint">{plan.notInCsv.map((o) => `${o.op_code} ${o.name}`).join('、')}</p>
            </details>
          )}
          {busy === 'scan' && <p className="ui-hint">ほかのデータ(日次集計・シフトなど)を確認しています…</p>}
          {refs && (
            <p className="ui-hint">
              {refs.writes.length === 0
                ? 'まとめるマスタを指しているほかのデータはありません。'
                : `まとめるマスタを指しているデータも書き換えます: ${Object.entries(refs.counts)
                    .map(([c, n]) => `${REISSUE_COLLECTION_LABELS[c] ?? c} ${formatCount(n)}件`)
                    .join('・')}`}
              {refs.conflicts.length > 0 && <span className="training-error"> ／ 両方に同じ人の分があり書き換えないもの {formatCount(refs.conflicts.length)}件: {refs.conflicts.join('、')}</span>}
            </p>
          )}
        </>
      )}
      <div className="training-actions">
        <button type="button" className="ui-btn ui-btn-primary" onClick={() => void run()} disabled={blocked || !!busy}>
          {busy === 'apply' ? '付け直し中…' : summary ? `${formatCount(summary.persons)}名の BB ID を付け直す` : '付け直す'}
        </button>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={saveMapping} disabled={!plan || !!busy}>
          新旧の対応表を保存(CSV)
        </button>
        <button type="button" className="ui-btn ui-btn-secondary" onClick={onClose} disabled={busy === 'apply'}>
          やめる
        </button>
      </div>
    </Modal>
  );
}
